import React, { createContext } from 'react';
import { useTranslation } from 'react-i18next';
import DraftConflictModal from '@components/atoms/modals/draft-conflict-modal.tsx';
import { openCloudVersionInNewTab } from '@utils/localStorage/draft-conflict.ts';
import type {
  SharedTranscriptContextType,
  SharedTranscriptProviderProps
} from './shared-transcript-context-state';
import { useSharedTranscriptContextLogic } from './use-shared-transcript-context-logic';

const SharedTranscriptContext = createContext<
  SharedTranscriptContextType | undefined
>(undefined);

export function SharedTranscriptProvider({
  children,
  session,
  document,
  token
}: SharedTranscriptProviderProps) {
  const { t } = useTranslation();
  const {
    contextValue,
    conflict,
    resolveConflictKeepLocal,
    resolveConflictLoadCloud
  } = useSharedTranscriptContextLogic({ session, document, token });

  return (
    <SharedTranscriptContext.Provider value={contextValue}>
      {conflict ? (
        <DraftConflictModal
          isOpen
          localSavedAt={conflict.localTimestamp}
          serverSavedAt={conflict.serverTimestamp}
          localCount={conflict.localCount}
          serverCount={conflict.serverCount}
          unit={t('transcript-segments-unit', 'segments')}
          onKeepLocal={resolveConflictKeepLocal}
          onLoadCloud={resolveConflictLoadCloud}
          onViewCloud={openCloudVersionInNewTab}
        />
      ) : (
        children
      )}
    </SharedTranscriptContext.Provider>
  );
}

export function useSharedTranscript() {
  const context = React.useContext(SharedTranscriptContext);
  if (context === undefined) {
    throw new Error('useTranscript must be used within a TranscriptProvider');
  }
  return context;
}
